import { AssetFault, AssetFaultIndicator } from 'src/app/models/assetFault';
import { ContributionDto, ContributionSourceTypes } from '../../models/contribution';
import { IssueDto } from '../../models/issue';
import { FAULT_STATE_ACTIVE } from '../../issue-formatting';

// The faults an engine control module reports. The asset fault collection also carries
// Sensor, Go and Dvir rows, and none of those come off a diagnostic bus.
export const ECM_PROTOCOLS: ReadonlyArray<string> = ['J1939', 'J1708', 'OBDII'];

// The widest window a reader may ask for, and the one the dialog opens on.
export const FAULT_RANGE_MAX_DAYS = 30;
export const FAULT_RANGE_DEFAULT_DAYS = 7;

const DAY_MS = 86400000;

/**
 * The J1939 dash lamps, ranked by what they ask the driver to do.
 *
 * This is the only thing on an engine fault that says how bad the module thought it was:
 * `severity` arrives as zero on every J1939 row. It is deliberately not the product severity
 * scale, because this is the truck's own dash rather than our score.
 */
export const LAMP_NONE = 0;
export const LAMP_MALFUNCTION = 1;
export const LAMP_WARNING = 2;
export const LAMP_STOP = 3;

export type LampRank = 0 | 1 | 2 | 3;

// One report the truck sent.
export interface FaultPointVm {
  atMs: number;
  occurrenceCount: number;
  instanceCount: number;
  active: boolean;
  lamp: LampRank;
  // The module began the sequence again here: this report names a beginning other than
  // the one before it.
  restart: boolean;
}

export interface FaultSeriesVm {
  // Identity of the fault, and the key the chart groups on.
  protocol: string;
  controller: number;
  faultCode: number;
  failureMode: number;
  key: string;
  // Resolved from faultCodeId by the caller. Empty until that read answers.
  name: string;
  // The name a reader sees: the catalog name plus whatever tells it apart from its
  // neighbors. Set by the caller once the name has resolved. See faultNameQualifier.
  label: string;
  // What tells this fault from another of the same name, held on its own so the list can
  // keep it out of the ellipsis. See faultNameQualifier.
  qualifier: string;
  // Row text the caller composes once, so the template never calls translate per cycle.
  codeLabel: string;
  worstLampTitle: string;
  episodesTitle: string;
  faultCodeId: string;
  // Carried by OBDII rows, empty on J1939. Not a grouping key: see buildFaultSeries.
  description: string;
  points: FaultPointVm[];
  lastAtMs: number;
  latestCount: number;
  // The state of the last report inside the window, not the state of the fault today.
  active: boolean;
  // The worst lamp the module lit at any point in the window.
  worstLamp: LampRank;
  // How many separate runs the fault made. One means it never restarted.
  episodes: number;
  // This fault names a contribution on an open Issue for the asset.
  triggered: boolean;
  color: string;
  visible: boolean;
}

// A fault named by an Issue's contribution.
export interface IssueFaultRef {
  protocol: string;
  faultCode: number;
  failureMode: number;
}

type Listish<T> = T[] | null | undefined;

// The Issue's own fault stays in the brand color, so the reader's eye starts on the line
// the drawer behind the dialog is about. The rest are neutral hues that do not collide with
// the severity scale, which readers already read as state.
export const FAULT_SERIES_COLORS: ReadonlyArray<string> = [
  '#cc0000', '#16706b', '#4b4fa6', '#8a6a3a', '#7a4b6b', '#3f6b4a', '#8a3f52', '#4a6480',
];

/* --- The window ---------------------------------------------------------- */

export interface FaultRange {
  startMs: number;
  endMs: number;
}

// Local midnight, because the boxes the reader types into are local dates. Reading them as
// UTC would move the window by the offset and put a morning report in the day before.
export function dayStartMs(value: string): number {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!parts) return NaN;
  return new Date(+parts[1], +parts[2] - 1, +parts[3]).getTime();
}

export function toDateInput(atMs: number): string {
  const at = new Date(atMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

// Local midnight some whole days away from a moment, by the calendar rather than by
// milliseconds. A day that crosses a clock change is 23 or 25 hours long, so a fixed
// 24 hours lands an hour off the midnight and puts the wrong date in a box.
export function shiftDayStart(atMs: number, days: number): number {
  const at = new Date(atMs);
  return new Date(at.getFullYear(), at.getMonth(), at.getDate() + days).getTime();
}

// The end date is the day the reader named, so the window runs to the end of it rather than
// to its midnight. A window that stopped at midnight would drop everything reported that day.
export function dayEndMs(value: string): number {
  const start = dayStartMs(value);
  return isNaN(start) ? NaN : shiftDayStart(start, 1);
}

export function defaultRange(nowMs: number): FaultRange {
  return {
    startMs: shiftDayStart(nowMs, -(FAULT_RANGE_DEFAULT_DAYS - 1)),
    endMs: shiftDayStart(nowMs, 1),
  };
}

export function rangeDays(range: FaultRange): number {
  return Math.max(1, Math.round((range.endMs - range.startMs) / DAY_MS));
}

/**
 * Pulls whatever the reader asked for back inside the bounds.
 *
 * The end never runs past today, because the truck cannot have reported tomorrow. A start
 * after the end is read as one day. A span wider than the maximum keeps the end the reader
 * chose and moves the start forward, because the end is the part they were looking at.
 */
export function clampRange(range: FaultRange, nowMs: number): FaultRange {
  const today = shiftDayStart(nowMs, 1);
  const endMs = isNaN(range.endMs) ? today : Math.min(range.endMs, today);
  let startMs = isNaN(range.startMs) ? shiftDayStart(endMs, -FAULT_RANGE_DEFAULT_DAYS) : range.startMs;
  if (startMs >= endMs) startMs = shiftDayStart(endMs, -1);
  if (rangeDays({ startMs, endMs }) > FAULT_RANGE_MAX_DAYS) {
    startMs = shiftDayStart(endMs, -FAULT_RANGE_MAX_DAYS);
  }
  return { startMs, endMs };
}

export function rangeKey(range: FaultRange): string {
  return `${range.startMs}|${range.endMs}`;
}

/* --- The lamp ------------------------------------------------------------ */

const LAMP_BY_NAME: ReadonlyArray<{ name: string; rank: LampRank }> = [
  { name: 'redstoplamp', rank: LAMP_STOP },
  { name: 'protectwarninglamp', rank: LAMP_WARNING },
  { name: 'amberwarninglamp', rank: LAMP_WARNING },
  { name: 'malfunctionlamp', rank: LAMP_MALFUNCTION },
];

// The worst lamp lit on this report. Red stop asks the driver to park it, so it outranks
// everything; the malfunction lamp is an emissions notice and ranks lowest.
export function lampRankOf(indicators: Listish<AssetFaultIndicator>): LampRank {
  let worst: LampRank = LAMP_NONE;
  for (const indicator of indicators ?? []) {
    if (!indicator?.enabled) continue;
    const known = LAMP_BY_NAME.find(l => l.name === (indicator.name ?? '').toLowerCase());
    if (known && known.rank > worst) worst = known.rank;
  }
  return worst;
}

/* --- Building the series ------------------------------------------------- */

// The record types its dates as Date and the wire sends strings. Take either, and hand
// back NaN for anything that is not a real moment so the caller can drop the row.
function msOf(value: Date | string | null | undefined): number {
  if (!value) return NaN;
  const t = value instanceof Date ? value.getTime() : Date.parse(value as string);
  return isNaN(t) ? NaN : t;
}

export function faultKeyOf(fault: {
  protocol: string; controller: number; faultCode: number; failureMode: number;
}): string {
  return `${fault.protocol}|${fault.controller}|${fault.faultCode}|${fault.failureMode}`;
}

// Every fault named by a contribution on any of these Issues. A source that is not an
// Alert carries no fault, so an operator report and a DVIR defect contribute nothing here.
export function issueFaultRefs(issues: Listish<IssueDto>): IssueFaultRef[] {
  const out: IssueFaultRef[] = [];
  for (const issue of issues ?? []) {
    for (const contribution of (issue?.contributions ?? []) as ContributionDto[]) {
      if (contribution.sourceType !== ContributionSourceTypes.Alert) continue;
      for (const fault of contribution.evidence?.faults ?? []) {
        out.push({
          protocol: fault.protocol,
          faultCode: fault.faultCode,
          failureMode: fault.failureMode,
        });
      }
    }
  }
  return out;
}

// The contribution names a protocol, a fault code and a failure mode, and no controller.
// A series carries the controller as well, so one contribution can mark two series when
// two controllers report the same code. Both of them did trigger the Issue.
function isTriggered(series: FaultSeriesVm, refs: IssueFaultRef[]): boolean {
  return refs.some(r => r.protocol === series.protocol
    && r.faultCode === series.faultCode
    && r.failureMode === series.failureMode);
}

/**
 * Groups a window of asset fault rows into one series per fault.
 *
 * Rows group on protocol, controller, fault code and failure mode. They never group on the
 * description: a sensor fault embeds its measured value there, so one fault produced four
 * different descriptions in a single week and would have drawn as four lines.
 */
export function buildFaultSeries(
  faults: Listish<AssetFault>,
  refs: IssueFaultRef[],
  range: FaultRange,
): FaultSeriesVm[] {
  const byKey = new Map<string, FaultSeriesVm>();
  const firstSeen = new Map<string, string[]>();

  for (const fault of faults ?? []) {
    if (!ECM_PROTOCOLS.includes(fault.protocol)) continue;
    const atMs = msOf(fault.latestDateTimeUtc);
    // The window filters on the latest report, the same predicate the query applies. A
    // fault with nothing inside the window is not in the list and not on the chart.
    if (isNaN(atMs) || atMs < range.startMs || atMs >= range.endMs) continue;

    const key = faultKeyOf(fault);
    let series = byKey.get(key);
    if (!series) {
      series = {
        protocol: fault.protocol,
        controller: fault.controller,
        faultCode: fault.faultCode,
        failureMode: fault.failureMode,
        key,
        name: '',
        label: '',
        qualifier: '',
        codeLabel: '',
        worstLampTitle: '',
        episodesTitle: '',
        faultCodeId: fault.faultCodeId ?? '',
        description: fault.description ?? '',
        points: [],
        lastAtMs: 0,
        latestCount: 0,
        active: false,
        worstLamp: LAMP_NONE,
        episodes: 0,
        triggered: false,
        color: '',
        visible: false,
      };
      byKey.set(key, series);
      firstSeen.set(key, []);
    }
    series.points.push({
      atMs,
      occurrenceCount: fault.occurrenceCount ?? 0,
      instanceCount: fault.instanceCount ?? 0,
      active: fault.faultState === FAULT_STATE_ACTIVE,
      lamp: lampRankOf(fault.indicators),
      restart: false,
    });
    // Kept beside the point rather than on it: the run a report belongs to is only knowable
    // once the reports are in order, which happens below.
    firstSeen.get(key)!.push(String(msOf(fault.firstDateTimeUtc)));
  }

  const out = [...byKey.values()];
  for (const series of out) {
    const starts = firstSeen.get(series.key)!;
    const order = series.points
      .map((point, i) => ({ point, start: starts[i] }))
      .sort((a, b) => a.point.atMs - b.point.atMs);
    series.points = order.map((entry, i) => ({
      ...entry.point,
      restart: i > 0 && entry.start !== order[i - 1].start,
    }));

    const last = series.points[series.points.length - 1];
    series.lastAtMs = last.atMs;
    series.latestCount = last.occurrenceCount;
    series.active = last.active;
    series.worstLamp = series.points.reduce(
      (worst, p) => (p.lamp > worst ? p.lamp : worst), LAMP_NONE as LampRank);
    series.episodes = 1 + series.points.filter(p => p.restart).length;
    series.triggered = isTriggered(series, refs);
  }

  // What opened an Issue leads, then whatever the truck reported most recently.
  out.sort((a, b) => Number(b.triggered) - Number(a.triggered) || b.lastAtMs - a.lastAtMs);
  out.forEach((series, i) => {
    series.color = FAULT_SERIES_COLORS[i % FAULT_SERIES_COLORS.length];
    series.visible = series.triggered;
  });
  return out;
}

/**
 * Splits a fault's reports into its separate runs.
 *
 * The chart draws one line per run and leaves a gap between them. A restart is a real
 * discontinuity: the module began counting that run again, so a line drawn straight through
 * would claim the count carried over from the run before.
 */
export function episodeRuns(points: FaultPointVm[]): FaultPointVm[][] {
  const runs: FaultPointVm[][] = [[]];
  for (const point of points ?? []) {
    if (point.restart && runs[runs.length - 1].length) runs.push([]);
    runs[runs.length - 1].push(point);
  }
  return runs.filter(run => run.length);
}

/**
 * What tells one fault from another of the same name.
 *
 * The catalog names a fault by its code alone, so the failure mode never reaches the name.
 * One truck reports SPN 111 under both FMI 18 and FMI 17, which gives two rows reading
 * "Engine coolant level 1" and nothing in the name to separate them. The list carries a
 * code column, but that column hides on a narrow screen and the hover readout has never
 * had one.
 *
 * Always appended, never only on a collision: a name that grows a suffix when the reader
 * widens the range would make the same fault read two different ways.
 *
 * OBDII carries no failure mode. Its rows carry the standard code instead, which is what
 * separates two of them.
 */
export function faultNameQualifier(series: {
  protocol: string; failureMode: number; description: string; name: string;
}): string {
  if (series.protocol === 'OBDII') {
    const code = (series.description ?? '').trim();
    return code && code !== series.name ? code : '';
  }
  return `FMI ${series.failureMode}`;
}

// Carries the reader's ticks across a change of range. A fault that leaves the window and
// comes back keeps the state they left it in; one they have never seen opens on its default.
export function applyVisibility(
  series: FaultSeriesVm[], chosen: ReadonlyMap<string, boolean>,
): FaultSeriesVm[] {
  for (const s of series) {
    const held = chosen.get(s.key);
    if (held !== undefined) s.visible = held;
  }
  return series;
}

/**
 * A smooth curve through the points, by monotone cubic interpolation (Fritsch-Carlson).
 *
 * An ordinary spline overshoots between two close points, and an overshoot here draws the
 * count dropping below a number the module already reported. The monotone form cannot do
 * that: it flattens a run the counter held, and never leaves the interval between two
 * neighboring values.
 *
 * Takes coordinates that are already scaled, and hands back an SVG path.
 */
export function monotonePath(xs: number[], ys: number[]): string {
  const n = Math.min(xs.length, ys.length);
  if (n === 0) return '';
  const r = (v: number) => Math.round(v * 10) / 10;
  // One report draws no path at all. A short tick keeps the fault from being invisible.
  if (n === 1) return `M ${r(xs[0] - 4)} ${r(ys[0])} L ${r(xs[0] + 4)} ${r(ys[0])}`;

  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = xs[i + 1] - xs[i];
    slope[i] = dx[i] > 0 ? (ys[i + 1] - ys[i]) / dx[i] : 0;
  }
  const m: number[] = [slope[0]];
  for (let i = 1; i < n - 1; i++) {
    m[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
  }
  m[n - 1] = slope[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const h = a * a + b * b;
    if (h > 9) {
      const t = 3 / Math.sqrt(h);
      m[i] = t * a * slope[i];
      m[i + 1] = t * b * slope[i];
    }
  }

  let d = `M ${r(xs[0])} ${r(ys[0])}`;
  for (let i = 0; i < n - 1; i++) {
    const third = dx[i] / 3;
    d += ` C ${r(xs[i] + third)} ${r(ys[i] + m[i] * third)},`
      + ` ${r(xs[i + 1] - third)} ${r(ys[i + 1] - m[i + 1] * third)},`
      + ` ${r(xs[i + 1])} ${r(ys[i + 1])}`;
  }
  return d;
}

// Four gridlines, and every one of them a whole number. A top of 65 gives ticks at 16.25.
export function niceCeiling(value: number): number {
  for (const step of [1, 2, 5, 10, 20, 25, 50, 100, 250, 500, 1000, 2500, 5000]) {
    if (step * 4 >= value) return step * 4;
  }
  return Math.ceil(value / 4) * 4;
}

export interface AxisTickVm { atMs: number; label: string; }

/**
 * Ticks on a boundary the reader recognizes. A day of history reads by the clock, and
 * anything longer by the calendar, so a month does not label five arbitrary instants.
 *
 * Both step from the start of the window, which is a local midnight, so the first tick is
 * the first hour or day the reader asked for rather than an instant aligned to the epoch.
 */
export function axisTicks(range: FaultRange, lang: string): AxisTickVm[] {
  const out: AxisTickVm[] = [];
  const days = rangeDays(range);
  if (days <= 1) {
    const step = 4 * 3600000;
    for (let t = range.startMs; t <= range.endMs; t += step) {
      out.push({ atMs: t, label: new Date(t).toLocaleTimeString(lang, { hour: 'numeric' }) });
    }
    return out;
  }
  const stepDays = days <= 7 ? 1 : days <= 14 ? 2 : 5;
  for (let i = 0; ; i += stepDays) {
    const t = shiftDayStart(range.startMs, i);
    if (t > range.endMs) break;
    out.push({
      atMs: t,
      label: new Date(t).toLocaleDateString(lang, { month: 'short', day: 'numeric' }),
    });
  }
  return out;
}

// A fault the module reported across the whole window without ever moving its counter.
// The chart draws that as a flat line, which reads as a quiet truck rather than a fault
// standing all week, so the dialog says so in words underneath.
export function flatSeries(series: FaultSeriesVm[]): FaultSeriesVm[] {
  return series.filter(s => s.visible
    && s.points.length > 1
    && s.points[0].occurrenceCount === s.points[s.points.length - 1].occurrenceCount);
}
